import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  Users, Target, FileText, CheckCircle2, CreditCard, DollarSign,
  TrendingUp, RefreshCw, ArrowRight, Loader2,
} from "lucide-react";
import { stageLabel, translateStageDescription } from "../../lib/stageLabels";

function Metric({ label, value, sub, icon: Icon, accent = "sky" }) {
  const colors = { sky: "text-sky-400", emerald: "text-emerald-400", amber: "text-amber-400" };
  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-soft)] p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="tick-label">{label}</div>
        <Icon className={`w-4 h-4 ${colors[accent]}`} strokeWidth={1.8} />
      </div>
      <div className={`metric-number text-3xl ${colors[accent]}`}>{value}</div>
      {sub && <div className="tick-label mt-1 text-slate-500">{sub}</div>}
    </div>
  );
}

export default function InternalDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try { const r = await api.get("/internal/dashboard"); if (!cancelled) setData(r.data); }
      catch (e) { if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load"); }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (err) return <div className="text-rose-400">{err}</div>;
  if (!data) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-sky-400 animate-spin" /></div>;

  const t = data.totals; const r = data.rates;

  return (
    <div data-testid="ceo-dashboard">
      <div className="tick-label text-sky-400 mb-2">/ CEO Operations Dashboard</div>
      <h1 className="font-display font-black text-3xl text-slate-100 mb-8">Pipeline overview</h1>

      <div className="tick-label text-slate-500 mb-3">/ Funnel</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-soft)] mb-8">
        <Metric label="Prospects" value={t.prospects} icon={Target} testId="m-prospects" />
        <Metric label="Qualified" value={t.qualified} icon={CheckCircle2} />
        <Metric label="Proposals Sent" value={t.assessments_delivered} icon={FileText} />
        <Metric label="Customers" value={t.paid_customers} sub={`${t.repeat_customers} repeat`} icon={CreditCard} accent="emerald" />
      </div>

      <div className="tick-label text-slate-500 mb-3">/ Revenue &amp; Conversion</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-8">
        <Metric label="Revenue" value={`$${(t.revenue_usd || 0).toLocaleString()}`} sub={`${t.purchases} purchases`} icon={DollarSign} accent="emerald" />
        <Metric label="Prospect → Customer" value={`${r.prospect_to_customer_pct}%`} icon={TrendingUp} accent="emerald" />
        <Metric label="Repeat Customer Rate" value={`${r.repeat_customer_pct}%`} icon={RefreshCw} accent="amber" />
      </div>

      <div className="grid lg:grid-cols-2 gap-px bg-[var(--border-soft)] mb-8">
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="tick-label text-sky-400 mb-3">/ Pipeline Distribution</div>
          <div className="space-y-1.5">
            {Object.entries(data.pipeline_distribution || {}).map(([s, n]) => (
              <div key={s} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{stageLabel(s)}</span>
                <span className="metric-number text-sky-400">{n}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="tick-label text-sky-400 mb-3">/ Prospect Grade Distribution</div>
          <div className="space-y-1.5">
            {Object.entries(data.grade_distribution || {}).map(([g, n]) => (
              <div key={g} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Grade {g}</span>
                <span className="metric-number text-sky-400">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-px bg-[var(--border-soft)]">
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="tick-label text-sky-400">/ Follow-Up Queue</div>
            <Link to="/internal/prospects" className="font-mono text-[10px] text-slate-400 hover:text-sky-400 tracking-widest uppercase">All Prospects <ArrowRight className="w-3 h-3 inline" /></Link>
          </div>
          {(data.follow_ups || []).length === 0 ? (
            <div className="text-slate-500 text-sm italic py-4">No follow-ups due in the next 7 days.</div>
          ) : (
            <div className="space-y-1.5 max-h-[300px] overflow-auto">
              {data.follow_ups.map((f) => (
                <Link to={`/internal/prospects/${f.prospect_id}`} key={f.prospect_id} className={`block p-2.5 border-l-2 ${f.overdue ? "border-rose-500 bg-rose-500/5" : "border-amber-500/40 bg-amber-500/5"} hover:bg-[var(--bg-primary)]`}>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-100 text-sm">{f.company_name}</span>
                    <span className="font-mono text-[10px] text-slate-400">{(f.next_follow_up_date || "").slice(0, 10)}</span>
                  </div>
                  <div className="font-mono text-[10px] text-slate-500">{stageLabel(f.stage)}{f.overdue ? " · OVERDUE" : ""}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="tick-label text-sky-400 mb-3">/ Recent Activity</div>
          {(data.recent_activity || []).length === 0 ? (
            <div className="text-slate-500 text-sm italic py-4">No activity yet.</div>
          ) : (
            <div className="space-y-px bg-[var(--border-soft)] max-h-[300px] overflow-auto">
              {data.recent_activity.map((a) => (
                <div key={a.activity_id} className="bg-[var(--bg-secondary)] p-2.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-mono text-[10px] text-sky-400 uppercase tracking-widest">{a.activity_type}</span>
                    <span className="font-mono text-[9px] text-slate-500">{(a.created_at || "").slice(11, 19)}</span>
                  </div>
                  <div className="text-[12px] text-slate-300 mt-0.5">{a.activity_type === "stage_change" ? translateStageDescription(a.description) : a.description}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
